import type { ChatEmoticonItem } from '@module/apis/client-side/chat-emoticon/type';
import { aspCond } from '@module/helpers/conditions/condition';
import type { StateCreator } from 'zustand';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  isChatNudgeSentSafeLocalStorage,
  pandatvFontSizeSafeLocalStorage,
} from '../lib/storage/storageImplementations';

type Orientation = 'landscape' | 'portrait';
export type LayoutKey = `vp${Capitalize<Orientation>}__vid${Capitalize<Orientation>}`;
export type ChatLayoutState = {
  layoutKey: LayoutKey;
  pl: boolean; // viewport portrait, video landscape
  pp: boolean; // viewport portrait, video portrait
  l: boolean; // viewport landscape (video any)
};

const layoutKeyFrom = (viewport: Orientation, video: Orientation): LayoutKey =>
  `vp${viewport === 'portrait' ? 'Portrait' : 'Landscape'}__vid${video === 'portrait' ? 'Portrait' : 'Landscape'}` as LayoutKey;

const createChatLayoutState = (deviceOrientation: Orientation, videoAspect: Orientation): ChatLayoutState => ({
  layoutKey: layoutKeyFrom(deviceOrientation, videoAspect),
  pl: deviceOrientation === 'portrait' && videoAspect === 'landscape',
  pp: deviceOrientation === 'portrait' && videoAspect === 'portrait',
  l: deviceOrientation === 'landscape',
});

export const DEFAULT_MOBILE_FONT_SIZE = 13;
export const DEFAULT_PC_FONT_SIZE = 13;
export const MAX_MOBILE_FONT_SIZE = 23;
export const MIN_MOBILE_FONT_SIZE = 13;

const IFRAME_ASP_FONTSIZE = 15;

const getPcFontSize = () => {
  if (typeof window !== 'undefined') {
    const fontSize = pandatvFontSizeSafeLocalStorage.get();
    if (fontSize) return fontSize;
    if (aspCond.if('iframe-asp')) return IFRAME_ASP_FONTSIZE;
    return DEFAULT_PC_FONT_SIZE;
  }
  return DEFAULT_PC_FONT_SIZE;
};

const setPcFontSizeHandler = (size: number) => {
  if (typeof window !== 'undefined') {
    pandatvFontSizeSafeLocalStorage.set(size);
  }
};

interface ChatState {
  isChatPanelOpen: boolean;
  openChatPanel: () => void;
  closeChatPanel: () => void;
  isChatInputAreaOpen: boolean;
  toggleChatInputArea: () => void;
  closeChatInputArea: () => void;
  isChatInputFocused: boolean;
  setIsChatInputFocused: (focused: boolean) => void;
  deviceOrientation: Orientation; // 시청 방향
  setDeviceOrientation: (orientation: Orientation) => void;
  videoAspect: Orientation; // 송출 화면비
  setVideoAspect: (aspect: Orientation) => void;
  getLayoutKey: () => LayoutKey;

  isViewportPortraitVidLandscape: () => boolean;
  isViewportPortraitVidPortrait: () => boolean;
  isViewportLandscape: () => boolean;
  fontSize: number;
  setFontSize: (size: number) => void;
  pcFontSize: number;
  setPcFontSize: (size: number) => void;
  isChatNudgeSent: boolean;
  setIsChatNudgeSent: (sent: boolean) => void;
  isChatHidden: boolean;
  setIsChatHidden: (isHidden: boolean) => void;
  isAppInstallPopUp: boolean;
  setIsAppInstallPopUp: (popup: boolean) => void;
  isChattingSettingOpen: boolean;
  setIsChattingSettingOpen: (open: boolean) => void;
  toggleChattingSetting: () => void;
  isFanListPopupOpen: boolean;
  setIsFanListPopupOpen: (open: boolean) => void;
  toggleFanListPopup: () => void;
  isChatListCollapsed: boolean;
  setIsChatListCollapsed: (collapsed: boolean) => void;
  isChatEmoticonSelectBoxOpen: boolean;
  setIsChatEmoticonSelectBoxOpen: (open: boolean) => void;
  selectedBlockEmoticon: ChatEmoticonItem | null;
  setSelectedBlockEmoticon: (selectedBlockEmoticon: ChatEmoticonItem | null) => void;
  clearSelectedBlockEmoticon: () => void;
  shouldForceDarkStyle: () => boolean;
  chatLayout: ChatLayoutState;
}

const store: StateCreator<ChatState, [], [], ChatState> = (set, get) => ({
  isChatPanelOpen: true,
  chatLayout: createChatLayoutState('portrait', 'landscape'),
  openChatPanel: () => set(() => ({ isChatPanelOpen: true })),
  closeChatPanel: () => set(() => ({ isChatPanelOpen: false })),
  isChatInputAreaOpen: false,
  isChatInputFocused: false,
  toggleChatInputArea: () =>
    set((state) => ({
      isChatInputAreaOpen: !state.isChatInputAreaOpen,
      isChatInputFocused: false,
      selectedBlockEmoticon: null,
    })),
  closeChatInputArea: () =>
    set(() => ({
      isChatInputAreaOpen: false,
      isChatInputFocused: false,
      isChatEmoticonSelectBoxOpen: false,
      selectedBlockEmoticon: null,
    })),
  setIsChatInputFocused: (focused: boolean) => set(() => ({ isChatInputFocused: focused })),
  deviceOrientation: 'portrait',
  setDeviceOrientation: (orientation: Orientation) => {
    const state = get();
    if (state.deviceOrientation === orientation) return;
    set({
      deviceOrientation: orientation,
      chatLayout: createChatLayoutState(orientation, state.videoAspect),
    });
  },
  videoAspect: 'landscape',
  setVideoAspect: (aspect: Orientation) => {
    const state = get();
    if (state.videoAspect === aspect) return;
    set({
      videoAspect: aspect,
      chatLayout: createChatLayoutState(state.deviceOrientation, aspect),
    });
  },
  getLayoutKey: () => {
    const { deviceOrientation, videoAspect } = get();
    return layoutKeyFrom(deviceOrientation, videoAspect);
  },

  isViewportPortraitVidLandscape: () => {
    const { deviceOrientation, videoAspect } = get();

    return deviceOrientation === 'portrait' && videoAspect === 'landscape';
  },
  isViewportPortraitVidPortrait: () => {
    const { deviceOrientation, videoAspect } = get();
    return deviceOrientation === 'portrait' && videoAspect === 'portrait';
  },
  isViewportLandscape: () => get().deviceOrientation === 'landscape',
  fontSize: DEFAULT_MOBILE_FONT_SIZE,
  setFontSize: (size: number) => set(() => ({ fontSize: size })),
  pcFontSize: getPcFontSize(),
  setPcFontSize: (size: number) => {
    setPcFontSizeHandler(size);
    set(() => ({ pcFontSize: size }));
  },
  isChatNudgeSent: !!isChatNudgeSentSafeLocalStorage.get(),
  setIsChatNudgeSent: (sent: boolean) => {
    isChatNudgeSentSafeLocalStorage.set(sent);
    set(() => ({ isChatNudgeSent: sent }));
  },
  isChatHidden: false,
  setIsChatHidden: (isChatHidden: boolean) => set(() => ({ isChatHidden })),
  isAppInstallPopUp: false,
  setIsAppInstallPopUp: (popup: boolean) => set(() => ({ isAppInstallPopUp: popup })),
  isChattingSettingOpen: false,
  setIsChattingSettingOpen: (open: boolean) => set(() => ({ isChattingSettingOpen: open })),
  toggleChattingSetting: () => set((state) => ({ isChattingSettingOpen: !state.isChattingSettingOpen })),
  isFanListPopupOpen: false,
  setIsFanListPopupOpen: (open: boolean) => set(() => ({ isFanListPopupOpen: open })),
  toggleFanListPopup: () => set((state) => ({ isFanListPopupOpen: !state.isFanListPopupOpen })),
  isChatListCollapsed: false,
  setIsChatListCollapsed: (collapsed: boolean) => set(() => ({ isChatListCollapsed: collapsed })),
  isChatEmoticonSelectBoxOpen: false,
  setIsChatEmoticonSelectBoxOpen: (open: boolean) => set(() => ({ isChatEmoticonSelectBoxOpen: open })),
  selectedBlockEmoticon: null,
  setSelectedBlockEmoticon: (selectedBlockEmoticon) => set(() => ({ selectedBlockEmoticon })),
  clearSelectedBlockEmoticon: () => set(() => ({ selectedBlockEmoticon: null })),
  shouldForceDarkStyle: () => {
    const { pp, l } = get().chatLayout;
    return pp || l;
  },
});

const useChatPanelStore = createWithEqualityFn<ChatState>()(store, shallow);

export default useChatPanelStore;
